import { extractYouTubeIdsFromMarkdown } from "@/lib/blog-helpers";
import { APP_LOCALES, type AppLocale } from "@/lib/locale";
import {
  SITE_ADDRESS,
  SITE_EMAIL,
  SITE_LEGAL_NAME,
  SITE_NAME,
  SITE_ORIGIN,
  SITE_PHONE_E164,
  SITE_SOCIAL,
} from "@/lib/site-info";
import { getLocale, localizeUrl, type Locale } from "@/paraglide/runtime";

export type SitemapPathEntry = {
  path: string;
  priority: string;
  changefreq: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
};

export const PUBLIC_SITEMAP_PATHS: SitemapPathEntry[] = [
  { path: "/", priority: "1.0", changefreq: "weekly" },
  { path: "/about", priority: "0.9", changefreq: "monthly" },
  { path: "/hall-of-fame", priority: "0.8", changefreq: "weekly" },
  { path: "/method", priority: "0.9", changefreq: "monthly" },
  { path: "/play-injury-free", priority: "0.8", changefreq: "monthly" },
  { path: "/lessons", priority: "0.9", changefreq: "monthly" },
  { path: "/schedule", priority: "0.9", changefreq: "weekly" },
  { path: "/prices", priority: "0.8", changefreq: "weekly" },
  { path: "/range", priority: "0.9", changefreq: "monthly" },
  { path: "/events", priority: "0.8", changefreq: "weekly" },
  { path: "/contact", priority: "0.7", changefreq: "monthly" },
  { path: "/blog", priority: "0.8", changefreq: "weekly" },
  { path: "/practice", priority: "0.8", changefreq: "weekly" },
];

/** Staff areas stay unprefixed across locales. */
const ROBOTS_DISALLOW_UNPREFIXED = ["/admin", "/employee"] as const;

/**
 * Member/auth/display paths are URL-localized (e.g. `/es/auth`).
 * Disallow both the English root and each locale prefix so crawlers
 * do not index private or non-marketing surfaces.
 */
const ROBOTS_DISALLOW_LOCALIZED = ["/auth", "/my-bookings", "/tv", "/reset-password"] as const;

const OG_LOCALE: Record<AppLocale, string> = {
  en: "en_US",
  es: "es_ES",
  de: "de_DE",
  zh: "zh_CN",
  ar: "ar_SA",
  fr: "fr_FR",
  vi: "vi_VN",
  sw: "sw_KE",
  th: "th_TH",
  bs: "bs_BA",
  ja: "ja_JP",
  ko: "ko_KR",
  pt: "pt_BR",
  it: "it_IT",
  sv: "sv_SE",
  nl: "nl_NL",
  da: "da_DK",
  nb: "nb_NO",
  fi: "fi_FI",
  pl: "pl_PL",
  ru: "ru_RU",
  tr: "tr_TR",
  hi: "hi_IN",
  id: "id_ID",
  ms: "ms_MY",
  cs: "cs_CZ",
  el: "el_GR",
  he: "he_IL",
  af: "af_ZA",
  hu: "hu_HU",
};

export type PageHeadInput = {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  /** Emit hreflang alternates (default true). Disable for untranslated blog posts. */
  hreflang?: boolean;
  /** Override active locale (defaults to getLocale()). */
  locale?: AppLocale;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

export type PageHeadLink = {
  rel: string;
  href: string;
  /** Lowercase HTML attr; TanStack Start SSR passes link keys as-is (camelCase hrefLang would emit invalid markup). */
  hreflang?: string;
};

export type PageHeadResult = {
  meta: Array<Record<string, string>>;
  links?: PageHeadLink[];
  scripts?: Array<{ type: string; children: string }>;
};

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_ORIGIN}${path}`;
}

export function canonicalPath(path: string): string {
  if (path === "/") return "/";
  return path.endsWith("/") ? path.slice(0, -1) : path;
}

/** Open Graph locale tag for a site locale. */
export function ogLocaleFor(locale: AppLocale | Locale): string {
  return OG_LOCALE[locale as AppLocale] ?? "en_US";
}

/**
 * Logical (unprefixed) path → localized path for a locale.
 * English stays unprefixed; others use `/{locale}/...`.
 */
export function localizedPath(path: string, locale: AppLocale | Locale): string {
  const logical = canonicalPath(path);
  const localized = localizeUrl(absoluteUrl(logical), { locale: locale as Locale });
  return canonicalPath(localized.pathname || "/");
}

/** Absolute URL for a logical path in a given locale. */
function localizedAbsoluteUrl(path: string, locale: AppLocale | Locale): string {
  return absoluteUrl(localizedPath(path, locale));
}

/** `rel=alternate` hreflang links for all app locales plus x-default (English). */
export function hreflangLinks(path: string): PageHeadLink[] {
  const links: PageHeadLink[] = APP_LOCALES.map((locale) => ({
    rel: "alternate",
    hreflang: locale,
    href: localizedAbsoluteUrl(path, locale),
  }));
  links.push({
    rel: "alternate",
    hreflang: "x-default",
    href: localizedAbsoluteUrl(path, "en"),
  });
  return links;
}

export function pageHead(input: PageHeadInput): PageHeadResult {
  const path = canonicalPath(input.path);
  const locale = (input.locale ?? getLocale()) as AppLocale;
  const url = localizedAbsoluteUrl(path, locale);
  const type = input.type ?? "website";
  const image = input.image ? absoluteUrl(input.image) : undefined;
  const emitHreflang = input.hreflang !== false && !input.noindex;

  const meta: Array<Record<string, string>> = [
    { title: input.title },
    { name: "description", content: input.description },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:locale", content: ogLocaleFor(locale) },
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
  ];

  for (const other of APP_LOCALES) {
    if (other === locale) continue;
    meta.push({ property: "og:locale:alternate", content: ogLocaleFor(other) });
  }

  if (image) {
    meta.push({ property: "og:image", content: image });
    meta.push({ name: "twitter:image", content: image });
  }

  if (input.noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }

  const result: PageHeadResult = { meta };

  if (!input.noindex) {
    const links: PageHeadLink[] = [{ rel: "canonical", href: url }];
    if (emitHreflang) {
      links.push(...hreflangLinks(path));
    }
    result.links = links;
  }

  if (input.jsonLd) {
    const nodes = Array.isArray(input.jsonLd) ? input.jsonLd : [input.jsonLd];
    result.scripts = nodes.map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    }));
  }

  return result;
}

export type SitemapUrlEntry = {
  loc: string;
  lastmod?: string;
  priority?: string;
  changefreq?: string;
};

/** Expand marketing paths across all locales; blog/practice detail paths stay English-only. */
export function buildLocalizedSitemapEntries(
  pages: SitemapPathEntry[],
  englishOnlyPaths: Array<{ path: string; lastmod?: string }> = [],
): SitemapUrlEntry[] {
  const entries: SitemapUrlEntry[] = [];

  for (const page of pages) {
    for (const locale of APP_LOCALES) {
      entries.push({
        loc: localizedAbsoluteUrl(page.path, locale),
        priority: page.priority,
        changefreq: page.changefreq,
      });
    }
  }

  for (const entry of englishOnlyPaths) {
    entries.push({
      loc: absoluteUrl(canonicalPath(entry.path)),
      lastmod: entry.lastmod,
      priority: "0.7",
      changefreq: "monthly",
    });
  }

  return entries;
}

export function buildSitemapXml(entries: SitemapUrlEntry[]): string {
  const body = entries
    .map((entry) => {
      const parts = [`    <loc>${escapeXml(entry.loc)}</loc>`];
      if (entry.lastmod) parts.push(`    <lastmod>${escapeXml(entry.lastmod)}</lastmod>`);
      if (entry.changefreq)
        parts.push(`    <changefreq>${escapeXml(entry.changefreq)}</changefreq>`);
      if (entry.priority) parts.push(`    <priority>${escapeXml(entry.priority)}</priority>`);
      return `  <url>\n${parts.join("\n")}\n  </url>`;
    })
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${body}
</urlset>
`;
}

export function buildRobotsTxt(): string {
  const lines: string[] = [
    "# markeglysports.com — crawl rules for search engines",
    "User-agent: *",
    "Allow: /",
    "",
  ];

  for (const path of ROBOTS_DISALLOW_UNPREFIXED) {
    lines.push(`Disallow: ${path}`);
  }

  for (const path of ROBOTS_DISALLOW_LOCALIZED) {
    lines.push(`Disallow: ${path}`);
    for (const locale of APP_LOCALES) {
      if (locale === "en") continue;
      lines.push(`Disallow: /${locale}${path}`);
    }
  }

  lines.push("");
  lines.push(`Sitemap: ${SITE_ORIGIN}/sitemap.xml`);
  lines.push("");
  return lines.join("\n");
}

export function localBusinessJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": ["SportsActivityLocation", "LocalBusiness"],
    name: SITE_NAME,
    alternateName: SITE_LEGAL_NAME,
    url: SITE_ORIGIN,
    telephone: SITE_PHONE_E164,
    email: SITE_EMAIL,
    image: absoluteUrl("/favicon.svg"),
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE_ADDRESS.street,
      addressLocality: SITE_ADDRESS.city,
      addressRegion: SITE_ADDRESS.state,
      postalCode: SITE_ADDRESS.zip,
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.6545,
      longitude: -93.6749,
    },
    sameAs: Object.values(SITE_SOCIAL),
    founder: {
      "@type": "Person",
      name: "Mark Egly",
      jobTitle: "PGA Golf Instructor",
      url: absoluteUrl("/about"),
    },
    areaServed: {
      "@type": "City",
      name: "Des Moines",
    },
  };
}

export function personJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Mark Egly",
    jobTitle: "PGA Golf Instructor",
    url: absoluteUrl("/about"),
    worksFor: {
      "@type": "SportsActivityLocation",
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
    sameAs: [SITE_SOCIAL.linkedin, SITE_SOCIAL.youtube, SITE_SOCIAL.facebook],
    address: {
      "@type": "PostalAddress",
      addressLocality: SITE_ADDRESS.city,
      addressRegion: SITE_ADDRESS.state,
      addressCountry: "US",
    },
  };
}

export function blogPostJsonLd(input: {
  title: string;
  description: string;
  path: string;
  image?: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    mainEntityOfPage: absoluteUrl(input.path),
    image: input.image ? absoluteUrl(input.image) : undefined,
    datePublished: input.publishedAt ?? undefined,
    dateModified: input.updatedAt ?? input.publishedAt ?? undefined,
    author: {
      "@type": "Person",
      name: "Mark Egly",
      url: absoluteUrl("/about"),
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  };
}

export function practiceDrillJsonLd(input: {
  title: string;
  description: string;
  path: string;
  image?: string;
  bodyMarkdown?: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
}): Record<string, unknown> | Record<string, unknown>[] {
  const article: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    mainEntityOfPage: absoluteUrl(input.path),
    image: input.image ? absoluteUrl(input.image) : undefined,
    datePublished: input.publishedAt ?? undefined,
    dateModified: input.updatedAt ?? input.publishedAt ?? undefined,
    author: {
      "@type": "Person",
      name: "Mark Egly",
      url: absoluteUrl("/about"),
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  };

  const youtubeId = input.bodyMarkdown
    ? (extractYouTubeIdsFromMarkdown(input.bodyMarkdown)[0] ?? null)
    : null;
  if (!youtubeId) return article;

  const video: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: input.title,
    description: input.description,
    thumbnailUrl: input.image ? absoluteUrl(input.image) : undefined,
    uploadDate: input.publishedAt ?? undefined,
    embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
  };

  return [article, video];
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}
